import { ChangeDetectionStrategy, Component, computed, inject, type TemplateRef } from '@angular/core';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { TranslatePipe } from '@ngx-translate/core';
import { ZagButton } from '../button/button';
import { ZagIcon } from '../icon/icon';
import { BulkEditJobResultListComponent } from './bulk-edit-job-result-list.component';
import type { BulkJobRehydration, BulkJobStatus, BulkTotals, BulkBuckets } from './bulk-edit.models';
import type { BulkJobToastData } from './bulk-edit-inline.types';

export interface BulkEditJobDetailsDialogData {
  job: BulkJobToastData;
  rehydration: BulkJobRehydration;
  titleId?: string;
  descriptionId?: string;
  entityNameResolver?: (entityId: string) => string | undefined;
  entityResolver?: (entityId: string) => unknown | undefined;
  resultRowTemplate?: TemplateRef<{ $implicit: unknown }>;
}

const TOTAL_METRICS = [
  { key: 'succeeded', labelKey: 'bulkEdit.status.succeeded' },
  { key: 'failed', labelKey: 'bulkEdit.status.failed' },
  { key: 'skipped', labelKey: 'bulkEdit.status.skippedAlreadyAssigned' },
  { key: 'cancelled', labelKey: 'bulkEdit.status.cancelled' },
] as const satisfies readonly { key: keyof BulkTotals; labelKey: string }[];

const BUCKET_METRICS = [
  { key: 'new', labelKey: 'bulkEdit.buckets.new' },
  { key: 'alreadyAssigned', labelKey: 'bulkEdit.buckets.alreadyAssigned' },
  { key: 'conflictOpen', labelKey: 'bulkEdit.buckets.conflictOpen' },
  { key: 'historicalOnly', labelKey: 'bulkEdit.buckets.historicalOnly' },
] as const satisfies readonly { key: keyof BulkBuckets; labelKey: string }[];

const STATUS_ICONS: Record<BulkJobStatus, string> = {
  running: 'sync',
  finished: 'check-circle',
  failed: 'error',
  cancelled: 'cancel',
};

@Component({
  selector: 'zag-bulk-edit-job-details-dialog',
  templateUrl: './bulk-edit-job-details-dialog.component.html',
  styleUrl: './bulk-edit-job-details-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslatePipe, ZagButton, ZagIcon, BulkEditJobResultListComponent],
})
export class BulkEditJobDetailsDialogComponent {
  private static nextId = 0;

  protected readonly dialogRef = inject<DialogRef<void>>(DialogRef);
  protected readonly data = inject<BulkEditJobDetailsDialogData>(DIALOG_DATA);
  protected readonly titleId = this.data.titleId ?? `zag-bulk-edit-job-details-dialog-title-${BulkEditJobDetailsDialogComponent.nextId++}`;
  protected readonly descriptionId = this.data.descriptionId ?? `${this.titleId}-description`;

  protected readonly totalMetrics = TOTAL_METRICS;
  protected readonly bucketMetrics = BUCKET_METRICS;
  protected readonly terminalStatus = computed(() => this.resolveStatus());
  protected readonly statusIcon = computed(() => STATUS_ICONS[this.terminalStatus()]);
  protected readonly statusLabelKey = computed(() =>
    this.terminalStatus() === 'running' ? 'bulkEdit.jobRunning' : `bulkEdit.inline.status.${this.terminalStatus()}`
  );

  protected close(): void {
    this.dialogRef.close();
  }

  private resolveStatus(): BulkJobStatus {
    const summary = this.data.rehydration.job;
    if (summary.status) return summary.status;
    if (!summary.completedAt) return 'running';
    if ((summary.totals?.failed ?? 0) > 0) return 'failed';
    if ((summary.totals?.cancelled ?? 0) > 0) return 'cancelled';
    return 'finished';
  }
}
